import { useMemo, useState, type FunctionComponent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Empty, Modal, Select, Typography, message } from "antd";
import dayjs from "dayjs";

import {
  ChallengeParticipantSource,
  ChallengeParticipantStatus,
  ChallengeTaskType,
} from "@fitness-app/data-models/entities/Challenge";
import { HabitStatus } from "@fitness-app/data-models/entities/Habit";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { supabase } from "~/store/initializeStore";
import { challengesHabitsKeys } from "./queryKeys";

type ChallengeOptionRow = {
  id: string;
  name: string;
};

type HabitTaskRow = {
  habitTemplateId: string | null;
};

type Props = {
  open: boolean;
  traineeId: string;
  userId: string | null;
  joinedChallengeIds: string[];
  onClose: () => void;
};

const AddToChallengeModal: FunctionComponent<Props> = ({ open, traineeId, userId, joinedChallengeIds, onClose }) => {
  const queryClient = useQueryClient();
  const { memberId } = useUserClaims();
  const [selectedChallengeId, setSelectedChallengeId] = useState<string | null>(null);

  const challengesQuery = useQuery({
    queryKey: ["assignable-challenges"],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge")
        .select("id, name")
        .order("startDate", { ascending: false })
        .returns<ChallengeOptionRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const options = useMemo(() => {
    const joined = new Set(joinedChallengeIds);
    return (challengesQuery.data ?? [])
      .filter((challenge) => !joined.has(challenge.id))
      .map((challenge) => ({ value: challenge.id, label: challenge.name }));
  }, [challengesQuery.data, joinedChallengeIds]);

  const addMutation = useMutation({
    mutationFn: async (challengeId: string) => {
      const participantRow = {
        challengeId,
        traineeId,
        userId,
        trainerId: null,
        joinedAt: dayjs().toISOString(),
        source: ChallengeParticipantSource.Assigned,
        status: ChallengeParticipantStatus.Active,
      };

      // No ignoreDuplicates: a trainee who previously left must be flipped back to Active on conflict.
      const { error: participantError } = await supabase
        .from("challenge_participant")
        .upsert(participantRow as never, { onConflict: "challengeId,traineeId" });

      if (participantError) {
        throw new Error(participantError.message);
      }

      // Provision habit instances for every habit task on this challenge.
      const { data: habitTasks, error: tasksError } = await supabase
        .from("challenge_task")
        .select("habitTemplateId")
        .eq("challengeId", challengeId)
        .eq("type", ChallengeTaskType.Habit)
        .returns<HabitTaskRow[]>();

      if (tasksError) {
        throw new Error(tasksError.message);
      }

      const habitTemplateIds = (habitTasks ?? [])
        .map((task) => task.habitTemplateId)
        .filter((id): id is string => Boolean(id));

      if (habitTemplateIds.length > 0) {
        const today = dayjs().format("YYYY-MM-DD");
        const habitRows = habitTemplateIds.map((templateId) => ({
          templateId,
          traineeId,
          userId,
          createdBy: memberId ?? null,
          status: HabitStatus.Active,
          startDate: today,
        }));

        const { error: habitError } = await supabase
          .from("client_habit")
          .upsert(habitRows as never, { onConflict: "templateId,traineeId", ignoreDuplicates: true });

        if (habitError) {
          throw new Error(habitError.message);
        }
      }
    },
    onSuccess: () => {
      void message.success("Klient dodany do wyzwania");
      void queryClient.invalidateQueries({ queryKey: challengesHabitsKeys.challengeParticipants(traineeId) });
      void queryClient.invalidateQueries({ queryKey: challengesHabitsKeys.habits(traineeId) });
      setSelectedChallengeId(null);
      onClose();
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się dodać do wyzwania: ${error.message}`);
    },
  });

  return (
    <Modal
      title={<Typography.Text>Dodaj do wyzwania</Typography.Text>}
      open={open}
      onCancel={() => {
        setSelectedChallengeId(null);
        onClose();
      }}
      onOk={() => selectedChallengeId && addMutation.mutate(selectedChallengeId)}
      okText="Dodaj"
      cancelText="Anuluj"
      confirmLoading={addMutation.isLoading}
      okButtonProps={{ disabled: !selectedChallengeId }}
      destroyOnClose
    >
      {options.length === 0 && !challengesQuery.isFetching ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak wyzwań do wyboru" />
      ) : (
        <Select
          style={{ width: "100%" }}
          placeholder="Wybierz wyzwanie"
          loading={challengesQuery.isFetching}
          showSearch
          optionFilterProp="label"
          value={selectedChallengeId}
          onChange={setSelectedChallengeId}
          options={options}
        />
      )}
      <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
        Punktacja przeliczana jest automatycznie co 30 minut – nie musisz nic dodatkowo uruchamiać.
      </Typography.Paragraph>
    </Modal>
  );
};

export default AddToChallengeModal;
